/**
 * Cashflow V2 TanStack Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customInstance } from '../../services/api-client'
import type {
  Action,
  AcquisitionAiAssessment,
  AcquisitionTarget,
  AiOperations,
  AppletDataSourceConfig,
  AppletSystem,
  ApAgingRow,
  ArAgingRow,
  AvailabilitySummaryRow,
  ConnectorOption,
  DailyBankBalance,
  FacilityConfig,
  ForecastConfidenceRow,
  ForecastDriftRow,
  ForecastVersion,
  RevenueDetailRow,
  UnclearedAdjustment,
  VarianceDetailRow,
  VarianceNarrative,
  WcRecommendation,
  WeeklyForecastRow,
} from './types'

const BASE = '/api/cashflow-v2'
const SNAPSHOT_DATA_STALE_TIME = 15 * 60_000

const keys = {
  all: ['cashflow-v2'] as const,
  forecast: () => [...keys.all, 'forecast'] as const,
  facility: () => [...keys.all, 'facility'] as const,
  availability: () => [...keys.all, 'availability'] as const,
  uncleared: () => [...keys.all, 'uncleared-adjustments'] as const,
  dailyBank: () => [...keys.all, 'daily-bank-balances'] as const,
  confidence: () => [...keys.all, 'forecast-confidence'] as const,
  drift: () => [...keys.all, 'forecast-drift'] as const,
  versions: () => [...keys.all, 'forecast-versions'] as const,
  actions: () => [...keys.all, 'actions'] as const,
  arAging: () => [...keys.all, 'ar-aging'] as const,
  apAging: () => [...keys.all, 'ap-aging'] as const,
  revenue: () => [...keys.all, 'revenue-detail'] as const,
  varianceDetail: () => [...keys.all, 'variance-detail'] as const,
  varianceNarratives: () => [...keys.all, 'variance-narratives'] as const,
  workingCapital: () => [...keys.all, 'wc-recommendations'] as const,
  acquisitionTargets: () => [...keys.all, 'acquisition-targets'] as const,
  acquisitionAssessments: () => [...keys.all, 'acquisition-ai-assessments'] as const,
  aiOperations: () => [...keys.all, 'ai-operations'] as const,
  appletSystem: () => [...keys.all, 'applet-system'] as const,
  config: () => [...keys.all, 'config'] as const,
  connectors: () => [...keys.all, 'connectors'] as const,
  snapshotStatus: () => [...keys.all, 'snapshot-status'] as const,
} as const

export function useForecast() {
  return useQuery<WeeklyForecastRow[]>({
    queryKey: keys.forecast(),
    queryFn: () =>
      customInstance<{ rows: WeeklyForecastRow[] }>({ url: `${BASE}/forecast` }).then(
        response => response.rows,
      ),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useFacility() {
  return useQuery<FacilityConfig>({
    queryKey: keys.facility(),
    queryFn: () => customInstance({ url: `${BASE}/facility` }),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useAvailability() {
  return useQuery<AvailabilitySummaryRow[]>({
    queryKey: keys.availability(),
    queryFn: () =>
      customInstance<{ rows: AvailabilitySummaryRow[] }>({
        url: `${BASE}/availability`,
      }).then(response => response.rows),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useUnclearedAdjustments() {
  return useQuery<UnclearedAdjustment[]>({
    queryKey: keys.uncleared(),
    queryFn: () =>
      customInstance<{ items: UnclearedAdjustment[] }>({
        url: `${BASE}/uncleared-adjustments`,
      }).then(response => response.items),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useDailyBankBalances(limit = 400) {
  return useQuery<DailyBankBalance[]>({
    queryKey: [...keys.dailyBank(), limit] as const,
    queryFn: () =>
      customInstance<{ rows: DailyBankBalance[] }>({
        url: `${BASE}/daily-bank-balances?limit=${limit}`,
      }).then(response => response.rows),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useForecastConfidence() {
  return useQuery<ForecastConfidenceRow[]>({
    queryKey: keys.confidence(),
    queryFn: () =>
      customInstance<{ rows: ForecastConfidenceRow[] }>({
        url: `${BASE}/forecast-confidence`,
      }).then(response => response.rows),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useForecastDrift() {
  return useQuery<ForecastDriftRow[]>({
    queryKey: keys.drift(),
    queryFn: () =>
      customInstance<{ rows: ForecastDriftRow[] }>({
        url: `${BASE}/forecast-drift`,
      }).then(response => response.rows),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useForecastVersions() {
  return useQuery<ForecastVersion[]>({
    queryKey: keys.versions(),
    queryFn: () =>
      customInstance<{ rows: ForecastVersion[] }>({
        url: `${BASE}/forecast-versions`,
      }).then(response => response.rows),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useActions() {
  return useQuery<Action[]>({
    queryKey: keys.actions(),
    queryFn: () =>
      customInstance<{ items: Action[] }>({ url: `${BASE}/actions` }).then(
        response => response.items,
      ),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useArAging() {
  return useQuery<ArAgingRow[]>({
    queryKey: keys.arAging(),
    queryFn: () =>
      customInstance<{ items: ArAgingRow[] }>({ url: `${BASE}/ar-aging` }).then(
        response => response.items,
      ),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useApAging() {
  return useQuery<ApAgingRow[]>({
    queryKey: keys.apAging(),
    queryFn: () =>
      customInstance<{ items: ApAgingRow[] }>({ url: `${BASE}/ap-aging` }).then(
        response => response.items,
      ),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useRevenueDetail() {
  return useQuery<RevenueDetailRow[]>({
    queryKey: keys.revenue(),
    queryFn: () =>
      customInstance<{ items: RevenueDetailRow[] }>({
        url: `${BASE}/revenue-detail`,
      }).then(response => response.items),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useVarianceDetail() {
  return useQuery<VarianceDetailRow[]>({
    queryKey: keys.varianceDetail(),
    queryFn: () =>
      customInstance<{ items: VarianceDetailRow[] }>({
        url: `${BASE}/variance-detail`,
      }).then(response => response.items),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useVarianceNarratives() {
  return useQuery<VarianceNarrative[]>({
    queryKey: keys.varianceNarratives(),
    queryFn: () =>
      customInstance<{ items: VarianceNarrative[] }>({
        url: `${BASE}/variance-narratives`,
      }).then(response => response.items),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useWcRecommendations() {
  return useQuery<WcRecommendation[]>({
    queryKey: keys.workingCapital(),
    queryFn: () =>
      customInstance<{ items: WcRecommendation[] }>({
        url: `${BASE}/wc-recommendations`,
      }).then(response => response.items),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useAcquisitionTargets() {
  return useQuery<AcquisitionTarget[]>({
    queryKey: keys.acquisitionTargets(),
    queryFn: () =>
      customInstance<{ items: AcquisitionTarget[] }>({
        url: `${BASE}/acquisition-targets`,
      }).then(response => response.items),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useAcquisitionAiAssessments() {
  return useQuery<AcquisitionAiAssessment[]>({
    queryKey: keys.acquisitionAssessments(),
    queryFn: () =>
      customInstance<{ items: AcquisitionAiAssessment[] }>({
        url: `${BASE}/acquisition-ai-assessments`,
      }).then(response => response.items),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useAiOperations() {
  return useQuery<AiOperations>({
    queryKey: keys.aiOperations(),
    queryFn: () => customInstance({ url: `${BASE}/ai-operations` }),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useAppletSystem() {
  return useQuery<AppletSystem>({
    queryKey: keys.appletSystem(),
    queryFn: () => customInstance({ url: `${BASE}/applet-system` }),
    staleTime: SNAPSHOT_DATA_STALE_TIME,
  })
}

export function useDataSourceConfig() {
  return useQuery<AppletDataSourceConfig>({
    queryKey: keys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source` }),
    staleTime: 60_000,
  })
}

export function useAvailableConnectors() {
  return useQuery<ConnectorOption[]>({
    queryKey: keys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors` }),
    staleTime: 60_000,
  })
}

export interface AskElizaHistoryMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface SnapshotStatus {
  state: 'empty' | 'ready' | 'refreshing' | 'failed_refresh'
  published_run_id?: string | null
  published_at?: string | null
  dataset_counts: Record<string, number>
  refresh_run_id?: string | null
  refresh_status?: string | null
  refresh_reason?: string | null
  refresh_started_at?: string | null
  refresh_completed_at?: string | null
  error_message?: string | null
}

export interface SnapshotRefreshResponse {
  run_id: string
  status: string
  task_id?: string | null
  message: string
}

export function useSnapshotStatus() {
  return useQuery<SnapshotStatus>({
    queryKey: keys.snapshotStatus(),
    queryFn: () => customInstance({ url: `${BASE}/snapshot/status` }),
    staleTime: 10_000,
    refetchInterval: query =>
      query.state.data?.state === 'refreshing' ? 3_000 : 30_000,
  })
}

export function useRefreshSnapshot() {
  const queryClient = useQueryClient()
  return useMutation<SnapshotRefreshResponse, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/snapshot/refresh`,
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.snapshotStatus() })
    },
  })
}

export interface AskElizaRequest {
  question: string
  history?: AskElizaHistoryMessage[]
  model?: string
}

export interface AskElizaResponse {
  answer: string
  sources: string[]
  confidence: number
  model: string
  provider: string
  tokens_used?: number | null
}

export function useAskElizaSuggestions() {
  return useQuery<string[]>({
    queryKey: [...keys.all, 'ask-suggestions'] as const,
    queryFn: () =>
      customInstance<{ suggestions: string[] }>({
        url: `${BASE}/ask/suggestions`,
      }).then(r => r.suggestions),
    staleTime: 5 * 60_000,
  })
}

export function useAskEliza() {
  return useMutation<AskElizaResponse, Error, AskElizaRequest>({
    mutationFn: payload =>
      customInstance<AskElizaResponse>({
        url: `${BASE}/ask`,
        method: 'POST',
        data: payload,
      }),
  })
}

export function useSetDataSource() {
  const queryClient = useQueryClient()
  return useMutation<
    AppletDataSourceConfig,
    Error,
    { connector_id: string; redshift_schema?: string }
  >({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useClearDataSource() {
  const queryClient = useQueryClient()
  return useMutation<AppletDataSourceConfig, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useTestConnection() {
  return useMutation<
    { status: string; redshift_connected: boolean; message: string },
    Error,
    { connector_id: string; redshift_schema?: string }
  >({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  })
}

export { keys as cashflowV2QueryKeys }
